
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

interface BookingFormProps {
  onSubmit: (formData: BookingFormData) => void;
  isSubmitting: boolean;
}

export interface BookingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  guests: string;
  specialRequests: string;
  agreeTerms: boolean;
}

const BookingForm = ({ onSubmit, isSubmitting }: BookingFormProps) => {
  const [formData, setFormData] = useState<BookingFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    guests: "2",
    specialRequests: "",
    agreeTerms: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      agreeTerms: checked,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Prénom</Label>
          <Input
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Nom</Label>
          <Input
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="phone">Téléphone</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="guests">Nombre de voyageurs</Label>
        <Input
          id="guests"
          name="guests"
          type="number"
          min="1"
          max="8"
          value={formData.guests}
          onChange={handleChange}
          required
        />
        <p className="text-sm text-gray-500">
          La villa peut accueillir jusqu'à 8 personnes
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="specialRequests">
          Demandes spéciales (optionnel)
        </Label>
        <Textarea
          id="specialRequests"
          name="specialRequests"
          value={formData.specialRequests}
          onChange={handleChange}
          placeholder="Informez-nous de toute demande particulière"
          rows={4}
        />
      </div>
      
      <div className="flex items-start space-x-2">
        <Checkbox
          id="agreeTerms"
          checked={formData.agreeTerms}
          onCheckedChange={handleCheckboxChange}
        />
        <div className="grid gap-1.5 leading-none">
          <label
            htmlFor="agreeTerms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            J'accepte les{" "}
            <a
              href="/mentions-legales"
              className="text-villa-turquoise underline"
            >
              conditions générales
            </a>
          </label>
        </div>
      </div>
      
      <Button
        type="submit"
        className="w-full bg-villa-turquoise hover:bg-opacity-90 text-lg py-6"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Traitement en cours..." : "Confirmer la réservation"}
      </Button>
    </form>
  );
};

export default BookingForm;
